import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@sandtech-hub-monorepo/ui-kit';
import { GetViewerTranscriptQuery } from '@sandtech-hub-monorepo/ehub-client';
import {
  TranscriptView,
  TranscriptCourseEnrollment,
  TranscriptConductCase,
  TranscriptProgramEnrollment,
  TranscriptTermGPA,
  TranscriptMode,
} from '@sandtech-hub-monorepo/ui-kit-site';

type ProgramEnrollment =
  GetViewerTranscriptQuery['viewer']['programEnrollments'][number];

type ClassEnrollment = NonNullable<
  ProgramEnrollment['plannedCourses'][number]['enrollment']
>['classEnrollments'][number];

interface Props {
  viewer: GetViewerTranscriptQuery['viewer'];
  programEnrollments: ProgramEnrollment[];
}

const collectCourseEnrollments = (
  programEnrollments: ProgramEnrollment[]
): TranscriptCourseEnrollment[] => {
  const seen = new Set<string>();
  const result: TranscriptCourseEnrollment[] = [];

  for (const pe of programEnrollments) {
    for (const pc of pe.plannedCourses) {
      if (!pc.enrollment) continue;
      if (seen.has(pc.enrollment.id)) continue;
      seen.add(pc.enrollment.id);
      result.push({
        status: pc.enrollment.status ?? '',
        letterGrade: pc.enrollment.letterGrade,
        gradePointsValue: pc.enrollment.gradePointsValue,
        graduatedAt: pc.enrollment.graduatedAt,
        failedAt: pc.enrollment.failedAt,
        course: {
          name: pc.details.name,
          abbreviation: pc.details.abbreviation,
        },
        classEnrollments: pc.enrollment.classEnrollments.map(
          (cle: ClassEnrollment) => ({
            letterGrade: cle.letterGrade,
            gradePointsValue: cle.gradePointsValue,
            status: cle.status,
            graduatedAt: cle.graduatedAt,
            failedAt: cle.failedAt,
            class: {
              term: cle.class?.term ?? null,
              startDate: cle.class?.startDate ?? null,
              name: cle.class?.name ?? '',
            },
          })
        ),
      });
    }
  }

  return result;
};

const buildCreditMap = (
  programEnrollments: ProgramEnrollment[]
): Map<string, number> => {
  const credits = new Map<string, number>();
  for (const pe of programEnrollments) {
    for (const pc of pe.plannedCourses) {
      if (!credits.has(pc.details.abbreviation)) {
        credits.set(pc.details.abbreviation, pc.credits);
      }
    }
  }
  return credits;
};

const TranscriptContent = ({ viewer, programEnrollments }: Props) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<TranscriptMode>('academic');

  // When mode is set to 'official', wait for the official-transcript content
  // to render, then trigger the print dialog. After the dialog closes, revert
  // to the academic on-screen view so the static info page is hidden.
  useEffect(() => {
    if (mode !== 'official') return;
    const id = requestAnimationFrame(() => {
      window.print();
      setMode('academic');
    });
    return () => cancelAnimationFrame(id);
  }, [mode]);

  const visibleEnrollments = useMemo(
    () =>
      programEnrollments.filter(
        (e) => e.status === 'ONGOING' || e.status === 'GRADUATED'
      ),
    [programEnrollments]
  );

  const courseEnrollments = useMemo(
    () => collectCourseEnrollments(programEnrollments),
    [programEnrollments]
  );

  const creditsByCourseDef = useMemo(
    () => buildCreditMap(programEnrollments),
    [programEnrollments]
  );

  const primaryEnrollment = useMemo(
    () =>
      visibleEnrollments.find((e) => e.status === 'ONGOING') ??
      visibleEnrollments[0],
    [visibleEnrollments]
  );

  const conductCases: TranscriptConductCase[] = useMemo(() => {
    const cases: TranscriptConductCase[] = [];
    for (const pe of visibleEnrollments) {
      for (const c of pe.academicConductCases) {
        cases.push({
          severity: c.severity,
          category: c.category,
          status: c.status,
          resolutionType: c.resolutionType,
          term: c.term,
          className: c.className ?? null,
        });
      }
    }
    return cases;
  }, [visibleEnrollments]);

  const termGPAs: TranscriptTermGPA[] = useMemo(
    () =>
      (primaryEnrollment?.termGPAs ?? []).map((t) => ({
        term: t.term,
        gpa: t.gpa,
      })),
    [primaryEnrollment]
  );

  const transcriptProgramEnrollments: TranscriptProgramEnrollment[] = useMemo(
    () =>
      visibleEnrollments.map((e) => ({
        status: e.status,
        graduatedAt: e.graduatedAt,
        program: { name: e.program.name ?? '' },
      })),
    [visibleEnrollments]
  );

  return (
    <div className="max-w-5xl mx-auto flex flex-col gap-5 md:gap-9 w-full">
      {/* Page title bar with actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 w-full print:hidden">
        <Button
          variant="outlineFg"
          className="rounded-full gap-2"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <div className="flex items-center gap-4">
          {/*
          FUNCTIONALITY REMOVED FOR NOW. IT SHOULD SEND AN EMAIL FIRST AND THIS TRANSCRIPT PDF SHOULD BE REVIEWED BY FIT
          <Button
            variant="outline"
            className="rounded-full border-primary text-primary hover:bg-primary/5 px-6 md:px-8 h-10 text-sm"
            onClick={() => setMode('official')}
          >
            Request official transcript
          </Button> */}
          <Button
            className="rounded-full px-6 md:px-8 h-10 text-sm gap-2"
            onClick={() => window.print()}
          >
            <Download className="w-4 h-4" />
            Download as PDF
          </Button>
        </div>
      </div>

      {/* Transcript card (shared component) */}
      <TranscriptView
        user={viewer}
        programEnrollments={transcriptProgramEnrollments}
        courseEnrollments={courseEnrollments}
        creditsByCourseDef={creditsByCourseDef}
        cumulativeGPA={primaryEnrollment?.cumulativeGPA ?? null}
        termGPAs={termGPAs}
        conductCases={conductCases}
        mode={mode}
      />
    </div>
  );
};

export default TranscriptContent;
